import {
  email,
  required,
  useNotify,
  useRecordContext,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useFormContext } from "react-hook-form";
import { ReferenceInput } from "@/components/admin/reference-input";
import { TextInput } from "@/components/admin/text-input";
import { SelectInput } from "@/components/admin/select-input";
import { ArrayInput } from "@/components/admin/array-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { Button } from "@/components/ui/button";

import type { Sale, Contact } from "../types";
import { AutocompleteCompanyInput } from "../companies/AutocompleteCompanyInput.tsx";
import {
  CONTACT_STATUSES,
  CONTACT_TYPE_CHOICES,
  CONTACT_TYPE_LABEL_KEYS,
  PREFERRED_CHANNELS,
} from "../palmtree/constants";
import { translatePersonalInfoTypeLabel } from "./contactModel.ts";

const NO_CONTACT_STATUS = "__none__";

export const ContactInputs = ({
  statusOptional = false,
}: {
  statusOptional?: boolean;
}) => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  // Stored partner stays selectable on that row so edit does not drop it.
  const typeIds =
    record?.type === "partner"
      ? (["partner", ...CONTACT_TYPE_CHOICES] as const)
      : CONTACT_TYPE_CHOICES;

  return (
    <div className="flex flex-col gap-4 p-1">
      <SelectInput
        source="type"
        label="resources.contacts.fields.type"
        choices={typeIds.map((id) => ({
          id,
          name: translate(CONTACT_TYPE_LABEL_KEYS[id]),
        }))}
        validate={required()}
        helperText={translate("resources.contacts.type_change_hint")}
      />
      <TextInput source="first_name" validate={required()} helperText={false} />
      <TextInput source="last_name" helperText={false} />
      <TextInput source="title" helperText={false} />
      <ReferenceInput source="company_id" reference="companies" perPage={10}>
        <AutocompleteCompanyInput label="resources.contacts.fields.company_id" />
      </ReferenceInput>
      <SelectInput
        source="status"
        choices={[
          ...(statusOptional
            ? [
                {
                  id: NO_CONTACT_STATUS,
                  name: translate("resources.contacts.status.none"),
                },
              ]
            : []),
          ...CONTACT_STATUSES.map((value) => ({ id: value, name: value })),
        ]}
        emptyValue={statusOptional ? NO_CONTACT_STATUS : ""}
        format={
          statusOptional
            ? (value) =>
                value == null || value === "" ? NO_CONTACT_STATUS : value
            : undefined
        }
        parse={
          statusOptional
            ? (value) =>
                value === NO_CONTACT_STATUS || value === "" || value == null
                  ? null
                  : value
            : undefined
        }
        validate={statusOptional ? undefined : required()}
        helperText={false}
        translateChoice={false}
      />
      <SelectInput
        source="preferred_channel"
        choices={PREFERRED_CHANNELS.map((value) => ({
          id: value,
          name: value,
        }))}
        helperText={false}
      />
      <ContactEmailInput />
      <ContactPhoneInput />
    </div>
  );
};

const ContactEmailInput = () => {
  const translate = useTranslate();
  const { getValues, setValue } = useFormContext();
  const personalInfoTypes = [
    { id: "Work", name: translatePersonalInfoTypeLabel("Work", translate) },
    { id: "Home", name: translatePersonalInfoTypeLabel("Home", translate) },
    { id: "Other", name: translatePersonalInfoTypeLabel("Other", translate) },
  ];

  const handleEmailChange = (emailValue: string) => {
    const { first_name, last_name } = getValues();
    if (first_name || last_name || !emailValue) return;
    const [first, last] = emailValue.split("@")[0].split(".");
    setValue("first_name", first.charAt(0).toUpperCase() + first.slice(1));
    setValue(
      "last_name",
      last ? last.charAt(0).toUpperCase() + last.slice(1) : "",
    );
  };

  const handleEmailBlur = (event: { target: { value: string } }) => {
    handleEmailChange(event.target.value);
  };

  const handlePaste = (event: {
    clipboardData: { getData: (type: string) => string };
  }) => {
    const pasted = event.clipboardData.getData("text");
    if (pasted) handleEmailChange(pasted);
  };

  return (
    <ArrayInput source="email_jsonb" helperText={false}>
      <SimpleFormIterator
        inline
        disableReordering
        disableClear
        className="[&>ul>li]:border-b-0 [&>ul>li]:pb-0"
      >
        <TextInput
          source="email"
          className="w-full"
          helperText={false}
          label={false}
          placeholder={translate("resources.contacts.fields.email")}
          validate={email()}
          onPaste={handlePaste}
          onBlur={handleEmailBlur}
        />
        <SelectInput
          source="type"
          helperText={false}
          label={false}
          optionText="name"
          choices={personalInfoTypes}
          defaultValue="Work"
          className="w-24 min-w-24"
        />
      </SimpleFormIterator>
    </ArrayInput>
  );
};

const ContactPhoneInput = () => {
  const translate = useTranslate();
  const personalInfoTypes = [
    { id: "Work", name: translatePersonalInfoTypeLabel("Work", translate) },
    { id: "Home", name: translatePersonalInfoTypeLabel("Home", translate) },
    { id: "Other", name: translatePersonalInfoTypeLabel("Other", translate) },
  ];
  return (
    <ArrayInput source="phone_jsonb" helperText={false}>
      <SimpleFormIterator
        inline
        disableReordering
        disableClear
        className="[&>ul>li]:border-b-0 [&>ul>li]:pb-0"
      >
        <TextInput
          source="number"
          className="w-full"
          helperText={false}
          label={false}
          placeholder={translate("resources.contacts.fields.phone_number")}
        />
        <SelectInput
          source="type"
          helperText={false}
          label={false}
          optionText="name"
          choices={personalInfoTypes}
          defaultValue="Work"
          className="w-24 min-w-24"
        />
      </SimpleFormIterator>
    </ArrayInput>
  );
};

export const saleOptionRenderer = (choice: Sale) =>
  `${choice.first_name} ${choice.last_name}`;

export const ContactStatusSelector = () => {
  const record = useRecordContext<Contact>();
  const [update] = useUpdate<Contact>();
  const notify = useNotify();
  if (!record) return null;

  const handleStatusChange = (nextStatus: string) => {
    if (nextStatus === record.status) return;
    update(
      "contacts",
      {
        id: record.id,
        data: { status: nextStatus },
        previousData: record,
      },
      {
        mutationMode: "optimistic",
        onError: (error) => {
          notify(
            typeof error === "string"
              ? error
              : error?.message || "ra.notification.http_error",
            { type: "error" },
          );
        },
      },
    );
  };

  return (
    <div className="flex flex-wrap gap-2">
      {CONTACT_STATUSES.map((status) => (
        <Button
          key={status}
          type="button"
          size="sm"
          variant={record.status === status ? "default" : "outline"}
          onClick={() => handleStatusChange(status)}
        >
          {status}
        </Button>
      ))}
    </div>
  );
};
